import { useReactiveVar } from '@apollo/client';
import { Route, Routes, useLocation, useNavigate, useParams } from '@hulu/react-router-dom';
import React, { useContext, useEffect } from 'react';

import useGetAdvertisersByOrgId from '../../../apis/graphql/advertisers/useGetAdvertisersByOrgId';
import useReportTemplate from '../../../apis/graphql/reportTemplates/useReportTemplate';
import { Loader, LoaderSize, LoaderType, PermittedPage } from '../../../components';
import LastUpdatedDate from '../../../components/baymax/LastUpdatedDate';
import { Notification } from '../../../components/BlueTheme';
import { NotificationType } from '../../../components/BlueTheme/Notification/types';
import {
  ArrowLeftWrapper,
  Breadcrumb,
  BreadcrumbText,
  PageHeader,
} from '../../../components/BlueTheme/PageHeader/styled';
import { TopBottomIndentsWrapper } from '../../../components/BlueTheme/Wrappers/styled';
import Footer from '../../../components/Footer';
import { selectedAdAccountIdVar, selectedOrganizationIdVar } from '../../../constants';
import { GlobalPermissionsContext } from '../../../context';
import { useHandleMetricsStatus } from '../../../hooks/baymax';
import { storage } from '../constants/reports';
import { ReportRoutes } from '../constants/routes';
import Preview from './Preview/Preview';
import { PageContent, StyledPageWrapper, TemplateName } from './styled';
import View from './View/View';

const Report = (): JSX.Element => {
  const { pathname } = useLocation();
  const { adEntityId, reportTemplateId } = useParams();
  const { permissions } = useContext(GlobalPermissionsContext);
  const navigate = useNavigate();
  const { data: reportTemplate, loading, error } = useReportTemplate(reportTemplateId);

  const orgId = useReactiveVar(selectedOrganizationIdVar) || '';
  const selectedAdAccountId = useReactiveVar(selectedAdAccountIdVar);
  const { adAccountList } = useGetAdvertisersByOrgId(orgId);
  const { delayDatesMessageToShow, lastUpdatedDateToShow, onCloseDelayDates } = useHandleMetricsStatus(
    adAccountList[adEntityId] || []
  );
  const isMixedPath = pathname.includes('mixed');

  useEffect(() => {
    if (selectedAdAccountId && !isMixedPath) {
      navigate(pathname?.replace('/reports/', '/reports/mixed/direct/'));
    }
  }, [isMixedPath, navigate, pathname, selectedAdAccountId]);

  const onBreadcrumbClick = (): void => {
    const isMixedPath = pathname.includes('mixed');

    storage.setAllStorageData({});
    navigate(`${isMixedPath ? '/direct' : '/reports'}/${adEntityId ? adEntityId : adAccountList[0]}`);
  };

  return (
    <StyledPageWrapper $isMixedPath={pathname.includes('mixed')}>
      {delayDatesMessageToShow && (
        <Notification
          type={NotificationType.warning}
          description={delayDatesMessageToShow}
          onClose={onCloseDelayDates}
        />
      )}
      <PageContent $notificationIsShowed={!!delayDatesMessageToShow}>
        <PageHeader>
          <Breadcrumb onClick={onBreadcrumbClick}>
            <ArrowLeftWrapper />
            <BreadcrumbText>Organization Overview</BreadcrumbText>
          </Breadcrumb>
          {lastUpdatedDateToShow && <LastUpdatedDate lastUpdatedDateText={lastUpdatedDateToShow} />}
        </PageHeader>
        {loading && <Loader size={LoaderSize.big} type={LoaderType.transparent} />}
        {error && (
          <TopBottomIndentsWrapper>
            <Notification type={NotificationType.error} description="Cannot get Report Data. Please try later." />
          </TopBottomIndentsWrapper>
        )}
        {!loading && !error && reportTemplate && (
          <>
            <TemplateName>{reportTemplate.name}</TemplateName>
            <Routes>
              <Route
                path={ReportRoutes.PREVIEW}
                element={
                  <PermittedPage permitted={permissions.BUILD_REPORT}>
                    <Preview reportTemplateSystemName={reportTemplate.systemName} templateName={reportTemplate.name} />
                  </PermittedPage>
                }
              />
              <Route
                path={ReportRoutes.VIEW}
                element={
                  <PermittedPage permitted>
                    <View reportTemplateSystemName={reportTemplate.systemName} templateName={reportTemplate.name} />
                  </PermittedPage>
                }
              />
            </Routes>
          </>
        )}
      </PageContent>
      <Footer isBaymax />
    </StyledPageWrapper>
  );
};

export default Report;
